// frontend/src/components/TaskModal/TaskModal.js

import React, { useState, useEffect } from 'react';
import './TaskModal.css';
import { FaTimes, FaPaperPlane, FaClock, FaFileUpload, FaCheckCircle, FaInfoCircle, FaSpinner, FaCalendar, FaProjectDiagram, FaExclamationCircle } from 'react-icons/fa';
import { supabase } from '../../supabaseClient';
import { fetchAuth } from '../../utils/api';
import { getUserData } from '../../utils/auth';

export default function TaskModal({ task, onClose, onTaskUpdated, onlyInfo = false, hidePlazo = false }) {
  const [activeTab, setActiveTab] = useState('info'); // 'info', 'entrega' o 'plazo'
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [detalleError, setDetalleError] = useState(null);
  const [detalleLoading, setDetalleLoading] = useState(false);
  const [progreso, setProgreso] = useState({ total: 0, completadas: 0, porcentaje: 0 });

  // Estados para Formulario 1: Notificar Entrega
  const [comentarios, setComentarios] = useState('');
  const [evidencia, setEvidencia] = useState(null);

  const [nuevaFecha, setNuevaFecha] = useState(task.fecha_termino ? String(task.fecha_termino).slice(0, 10) : '');
  const [motivo, setMotivo] = useState('');
  const isPlazoPending = String(task?.estado_plazo || '').toUpperCase() === 'PENDIENTE';
  const plazoSolicitadoHace = (() => {
    const d = task?.fecha_plazo_solicitud ? new Date(task.fecha_plazo_solicitud) : null;
    if (!d) return null;
    const diffMs = Date.now() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 60) return `${diffMin} min`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH} h`;
    const diffD = Math.floor(diffH / 24);
    return `${diffD} días`;
  })();

  useEffect(() => {
    setLoading(false);
    setSuccess(false);
    setError(null);
  }, [activeTab]);

  useEffect(() => {
    if (onlyInfo) {
      setActiveTab('info');
    }
  }, [onlyInfo]);

  useEffect(() => {
    if (hidePlazo && activeTab === 'plazo') {
      setActiveTab('info');
    }
  }, [hidePlazo, activeTab]);

  useEffect(() => {
    const loadDetalle = async () => {
      if (!task?.id_tarea) return;
      try {
        setDetalleLoading(true);
        setDetalleError(null);
        let d = null;
        let entregaDet = null;
        try {
          const u = await getUserData();
          const rol = String((u?.rol?.nombre) || '').toUpperCase();
          if (rol === 'JEFE') {
            d = await fetchAuth(`/api/jefe/tareas/${task.id_tarea}/detalle/`);
            if (task.id_compromiso) {
              try { entregaDet = await fetchAuth(`/api/entregas/${task.id_compromiso}/`); } catch {}
            }
          } else {
            d = await fetchAuth(`/api/colaborador/tareas/${task.id_tarea}/detalle/`);
            if (task.id_compromiso) {
              try { entregaDet = await fetchAuth(`/api/entregas/${task.id_compromiso}/`); } catch {}
            }
          }
        } catch (e) {
          try {
            d = await fetchAuth(`/api/colaborador/tareas/${task.id_tarea}/detalle/`);
          } catch {}
        }
        setDetalle({ ...(d || {}), ...(entregaDet || {}) } || null);
        try {
          const pid = d?.proyecto?.id_proyecto;
          if (pid) {
            const pd = await fetchAuth(`/api/colaborador/proyectos/${pid}/detalle/`);
            const tareas = Array.isArray(pd?.tareas) ? pd.tareas : [];
            const total = tareas.length;
            const norm = (s) => String(s || '').trim().toUpperCase().replace(/\s+/g, '_');
            const completadas = tareas.filter(t => norm(t.estado) === 'COMPLETADO').length;
            const porcentaje = total ? Math.round((completadas / total) * 100) : 0;
            setProgreso({ total, completadas, porcentaje });
          } else {
            setProgreso({ total: 0, completadas: 0, porcentaje: 0 });
          }
        } catch {}
      } catch (e) {
        setDetalleError(e?.message || 'No se pudo cargar detalle');
      } finally {
        setDetalleLoading(false);
      }
    };
    loadDetalle();
  }, [onlyInfo, task?.id_tarea]);

  
  const getMimeFromUrl = (u) => {
    const s = String(u || '').toLowerCase();
    if (s.endsWith('.pdf')) return 'application/pdf';
    if (s.endsWith('.png')) return 'image/png';
    if (s.endsWith('.jpg') || s.endsWith('.jpeg')) return 'image/jpeg';
    if (s.endsWith('.zip')) return 'application/zip';
    if (s.endsWith('.doc')) return 'application/msword';
    if (s.endsWith('.docx')) return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    if (s.endsWith('.xls')) return 'application/vnd.ms-excel';
    if (s.endsWith('.xlsx')) return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    return 'application/octet-stream';
  };

  const openEvidence = async (url) => {
    try {
      const res = await fetch(url);
      const buf = await res.blob();
      const mime = getMimeFromUrl(url);
      const blob = buf.type && buf.type !== 'application/octet-stream' ? buf : new Blob([buf], { type: mime });
      const objectUrl = URL.createObjectURL(blob);
      window.open(objectUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
    } catch {}
  };

  const downloadEvidence = async (url) => {
    try {
      const res = await fetch(url);
      const buf = await res.blob();
      const mime = getMimeFromUrl(url);
      const blob = buf.type && buf.type !== 'application/octet-stream' ? buf : new Blob([buf], { type: mime });
      const a = document.createElement('a');
      const objectUrl = URL.createObjectURL(blob);
      a.href = objectUrl;
      const parts = String(url || '').split('/');
      const fname = parts[parts.length - 1] || 'evidencia';
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
    } catch {}
  };

  const handleNotificarEntrega = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Obtener el token JWT
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) throw new Error('No hay sesión activa');

      const comentarioTrim = String(comentarios || '').trim();
      if (!comentarioTrim) {
        throw new Error('El comentario es obligatorio');
      }

      if (evidencia) {
        const name = String(evidencia.name || '');
        const ext = name.includes('.') ? name.split('.').pop().toLowerCase() : '';
        const allowed = ['pdf','doc','docx','zip','png','jpg','jpeg','xls','xlsx'];
        if (!allowed.includes(ext)) {
          throw new Error('Archivo no permitido');
        }
        const maxBytes = 10 * 1024 * 1024;
        if (evidencia.size && evidencia.size > maxBytes) {
          throw new Error('El archivo supera 10MB');
        }
      }

      const formData = new FormData();
      if (task.id_compromiso) {
        formData.append('id_compromiso', task.id_compromiso);
      } else {
        formData.append('id_tarea', task.id_tarea);
      }
      formData.append('comentario', comentarioTrim);
      if (evidencia) {
        formData.append('archivo', evidencia);
      }

      const data = await fetchAuth('/api/entregas/subir/', { method: 'POST', body: formData });
      console.log('✅ Entrega notificada:', data);
      
      setSuccess(true);
      setComentarios('');
      setEvidencia(null);
      
      setTimeout(() => {
        onTaskUpdated();
        onClose();
      }, 2000);

    } catch (err) {
      console.error('❌ Error al notificar entrega:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSolicitarPlazo = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) throw new Error('No hay sesión activa');

      const data = await fetchAuth('/api/solicitar-plazo/', { method: 'POST', body: JSON.stringify({ tarea_id: task.id_tarea, nueva_fecha: nuevaFecha, motivo }) });
      console.log('✅ Plazo solicitado:', data);

      setSuccess(true);
      setTimeout(() => {
        onTaskUpdated();
        onClose();
      }, 2000);

    } catch (err) {
      console.error('❌ Error al solicitar plazo:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-fadeIn"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-2xl w-full max-w-3xl border border-slate-200 shadow-2xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6 text-white">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h2 className="text-2xl font-bold mb-2">{detalle?.nombre || task.nombre}</h2>
              <p className="text-blue-100 text-sm flex items-center gap-2">
                <FaProjectDiagram className="text-sm" />
                {(detalle?.proyecto?.nombre_proyecto) || (typeof task.proyecto === 'object' ? (task.proyecto.nombre || 'Sin proyecto') : (task.proyecto || 'Sin proyecto'))}
              </p>
              {(() => {
                try {
                  const src = detalle?.descripcion ?? task.descripcion;
                  const m = String(src || '').match(/\[ETAPA:(\d+)\]/);
                  const id = m ? parseInt(m[1], 10) : null;
                  const map = { 1: 'Planificación', 2: 'Diseño', 3: 'Desarrollo', 4: 'Pruebas', 5: 'Implementación', 6: 'Cierre' };
                  const nombre = id ? (map[id] || `Etapa ${id}`) : (detalle?.etapa?.nombre || null);
                  return nombre ? <p className="text-blue-100 text-xs mt-1">Etapa: {nombre}</p> : null;
                } catch { return null; }
              })()}
              {progreso.total > 0 && (
                <div className="mt-3">
                  <div className="h-2 rounded-full bg-white/20 overflow-hidden">
                    {(() => {
                      const p = progreso.porcentaje || 0;
                      const color = p < 34 ? 'bg-red-400' : p < 67 ? 'bg-amber-400' : 'bg-emerald-400';
                      return <div className={`h-2 ${color} rounded-full`} style={{ width: `${p}%` }} />;
                    })()}
                  </div>
                  <p className="text-blue-100 text-xs mt-1">Progreso: {progreso.porcentaje}% · {progreso.completadas} de {progreso.total} completadas</p>
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-blue-100 transition-colors p-2 hover:bg-white/10 rounded-lg"
            >
              <FaTimes className="text-xl" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        {!onlyInfo && (
          <div className="px-6 pt-4 pb-3 border-b border-slate-200">
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setActiveTab('info')}
                aria-pressed={activeTab === 'info'}
                className={`group inline-flex items-center gap-2 px-4 py-2 rounded-full font-semibold text-sm transition-all focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                  activeTab === 'info'
                    ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${
                  activeTab === 'info' ? 'bg-white/20 text-white' : 'bg-slate-300 text-slate-700'
                }`}><FaInfoCircle className="text-xs" /></span>
                <span>Información</span>
              </button>

              <button
                onClick={() => setActiveTab('entrega')}
                aria-pressed={activeTab === 'entrega'}
                className={`group inline-flex items-center gap-2 px-4 py-2 rounded-full font-semibold text-sm transition-all focus:outline-none focus:ring-2 focus:ring-emerald-400 ${
                  activeTab === 'entrega'
                    ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${
                  activeTab === 'entrega' ? 'bg-white/20 text-white' : 'bg-slate-300 text-slate-700'
                }`}><FaPaperPlane className="text-xs" /></span>
                <span>Enviar Tarea</span>
              </button>

              {!hidePlazo && (
                <button
                  onClick={() => setActiveTab('plazo')}
                  aria-pressed={activeTab === 'plazo'}
                  className={`group inline-flex items-center gap-2 px-4 py-2 rounded-full font-semibold text-sm transition-all focus:outline-none focus:ring-2 focus:ring-amber-400 ${
                    activeTab === 'plazo'
                      ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${
                    activeTab === 'plazo' ? 'bg-white/20 text-white' : 'bg-slate-300 text-slate-700'
                  }`}><FaClock className="text-xs" /></span>
                  <span>Solicitar Plazo</span>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Tab: Información */}
          {activeTab === 'info' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                  <div className="flex items-center gap-2 mb-2">
                    <FaInfoCircle className="text-slate-400" />
                    <span className="text-xs font-medium text-slate-500 uppercase">Estado</span>
                  </div>
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold ${
                    (detalle?.estado || task.estado) === 'Completado' ? 'bg-emerald-100 text-emerald-700' :
                    (detalle?.estado || task.estado) === 'En Revisión' ? 'bg-amber-100 text-amber-700' :
                    (detalle?.estado || task.estado) === 'En Progreso' ? 'bg-blue-100 text-blue-700' :
                    (detalle?.estado || task.estado) === 'Pendiente' ? 'bg-slate-100 text-slate-700' :
                    'bg-slate-100 text-slate-700'
                  }`}>
                    {(detalle?.estado || task.estado) === 'Completado' && <FaCheckCircle className="text-xs" />}
                    {(detalle?.estado || task.estado) === 'En Revisión' && <FaSpinner className="text-xs" />}
                    {(detalle?.estado || task.estado) === 'En Progreso' && <FaSpinner className="text-xs" />}
                    {(detalle?.estado || task.estado) === 'Pendiente' && <FaClock className="text-xs" />}
                    {detalle?.estado || task.estado}
                  </span>
                </div>

                <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                  <div className="flex items-center gap-2 mb-2">
                    <FaCalendar className="text-slate-400" />
                    <span className="text-xs font-medium text-slate-500 uppercase">Fecha Límite</span>
                  </div>
                  <p className="font-semibold text-slate-800">
                    {(detalle?.fecha_termino || task.fecha_termino)
                      ? formatDate(detalle?.fecha_termino || task.fecha_termino)
                      : 'Sin fecha límite'}
                  </p>
                </div>

                <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                  <div className="flex items-center gap-2 mb-2">
                    <FaProjectDiagram className="text-slate-400" />
                    <span className="text-xs font-medium text-slate-500 uppercase">Proyecto</span>
                  </div>
                  <p className="font-semibold text-slate-800">{detalle?.proyecto?.nombre_proyecto || (typeof task.proyecto === 'object' ? (task.proyecto.nombre || '—') : (task.proyecto || '—'))}</p>
                </div>
              </div>

              {detalleLoading && (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-slate-600">Cargando detalle...</div>
              )}
              {detalleError && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">{detalleError}</div>
              )}

              {task.descripcion && (
                <></>
              )}

              {(() => {
                const desc = String((detalle?.descripcion ?? task.descripcion) || '');
                let cleaned = desc;
                let solicitud = null;
                try { cleaned = cleaned.replace(/\n?\[ETAPA:\d+\]/, ''); } catch {}
                if (desc.includes('[SOLICITUD_PLAZO]')) {
                  try {
                    const payload = desc.split('[SOLICITUD_PLAZO]')[1] || '';
                    const kvs = {};
                    payload.split(';').forEach(seg => {
                      const i = seg.indexOf('=');
                      if (i > -1) {
                        const k = seg.slice(0, i).trim();
                        const v = seg.slice(i + 1).trim();
                        if (k) kvs[k] = v;
                      }
                    });
                    cleaned = desc.replace(/\n?\[SOLICITUD_PLAZO\][\s\S]*$/, '');
                    solicitud = {
                      fecha_actual: kvs.fecha_actual || null,
                      nueva_fecha: kvs.nueva_fecha || null,
                      motivo: kvs.motivo || ''
                    };
                  } catch {}
                }
                try { cleaned = cleaned.replace(/\[ENTREGA_[A-Z_]+:[^\]]*\]/g, ''); } catch {}
                try { cleaned = cleaned.replace(/\[[^\]]+\]/g, ''); } catch {}
                try {
                  cleaned = cleaned
                    .replace(/\b[A-ZÁÉÍÓÚÑ_]{2,}=[^\s]+/g, ' ')
                    .replace(/(?:^|\s)([A-ZÁÉÍÓÚÑ_]+(?:\s+[A-ZÁÉÍÓÚÑ_]+)*)=\S+/g, ' ')
                    .replace(/\s{2,}/g, ' ');
                } catch {}
                try { cleaned = cleaned.replace(/\n{3,}/g, '\n\n'); } catch {}
                cleaned = cleaned.trim();

                return (
                  <>
                    {solicitud && (
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                        <h4 className="font-bold text-slate-800 mb-2 flex items-center gap-2">
                          <FaClock className="text-amber-600" />
                          Solicitud de Plazo Pendiente
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                          <div className="bg-white rounded-xl p-3 border border-amber-200">
                            <p className="text-xs text-slate-500">Fecha actual</p>
                            <p className="text-sm font-semibold text-slate-800">
                              {solicitud.fecha_actual ? formatDate(solicitud.fecha_actual) : '-'}
                            </p>
                          </div>
                          <div className="bg-white rounded-xl p-3 border border-amber-200">
                            <p className="text-xs text-slate-500">Nueva fecha solicitada</p>
                            <p className="text-sm font-semibold text-slate-800">
                              {solicitud.nueva_fecha ? formatDate(solicitud.nueva_fecha) : '-'}
                            </p>
                          </div>
                        </div>
                        {solicitud.motivo && (
                          <div>
                            <p className="text-xs text-slate-500 mb-1">Motivo</p>
                            <p className="text-sm text-slate-700">{solicitud.motivo}</p>
                          </div>
                        )}
                      </div>
                    )}
                    {cleaned && (
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                        <h4 className="font-bold text-slate-800 mb-2 flex items-center gap-2">
                          <FaInfoCircle className="text-blue-600" />
                          Descripción de la Tarea
                        </h4>
                        <p className="text-slate-700 text-sm leading-relaxed whitespace-pre-wrap">
                          {cleaned}
                        </p>
                      </div>
                    )}
                    {((detalle?.evidencia_url || detalle?.comentarios_entrega) || (detalle?.entrega?.archivo_url || detalle?.entrega?.comentario)) && (
                      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                        <h4 className="font-bold text-slate-800 mb-2 flex items-center gap-2">
                          <FaInfoCircle className="text-slate-600" />
                          Entrega del Colaborador
                        </h4>
                        {(detalle?.comentarios_entrega || detalle?.entrega?.comentario) && (
                          <p className="text-sm text-slate-700 mb-2">{detalle?.comentarios_entrega || detalle?.entrega?.comentario}</p>
                        )}
                    {(detalle?.evidencia_url || detalle?.entrega?.archivo_url) && (
                      (() => {
                        const url = String(detalle?.evidencia_url || detalle?.entrega?.archivo_url)
                          .replace('//storage','/storage')
                          .replace('/object/public/evidencias/entregas/','/object/public/entregas/')
                          .replace('/object/public/entregas/entregas/','/object/public/entregas/');
                        return (
                          <div className="flex items-center">
                            <button onClick={() => downloadEvidence(url)} className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-800 text-sm font-semibold rounded-lg">
                              Descargar
                            </button>
                          </div>
                        );
                      })()
                    )}
                      </div>
                    )}
                    {!cleaned && (
                      <div className="text-center py-8 text-slate-400">
                        <FaInfoCircle className="text-4xl mx-auto mb-2 opacity-30" />
                        <p className="text-sm">No hay descripción disponible para esta tarea</p>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {/* Tab: Notificar Entrega */}
          {activeTab === 'entrega' && (
            <form onSubmit={handleNotificarEntrega} className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-sm text-blue-800 flex items-start gap-2">
                  <FaInfoCircle className="mt-0.5 flex-shrink-0" />
                  <span>
                    Al enviar esta tarea, será revisada por el jefe de proyecto quien podrá aprobarla o solicitar correcciones.
                  </span>
                </p>
              </div>

              {/* Mensajes de éxito/error */}
              {success && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-2 text-emerald-800">
                  <FaCheckCircle className="text-xl" />
                  <span className="font-medium">¡Tarea enviada exitosamente!</span>
                </div>
              )}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-2 text-red-800">
                  <FaExclamationCircle className="text-xl mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Comentarios sobre tu trabajo <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={comentarios}
                  onChange={(e) => setComentarios(e.target.value)}
                  placeholder="Describe brevemente el trabajo realizado, dificultades encontradas, observaciones, etc."
                  rows={5}
                  required
                  disabled={loading || success}
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none disabled:bg-slate-50 disabled:text-slate-500"
                />
                <p className="text-xs text-slate-500 mt-1">Mínimo 10 caracteres</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Adjuntar Evidencia (Opcional)
                </label>
                <div className="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center hover:border-blue-400 transition-colors bg-slate-50">
                  <input
                    type="file"
                    onChange={(e) => setEvidencia(e.target.files[0])}
                    accept=".pdf,.doc,.docx,.zip,.jpg,.jpeg,.png,.xlsx,.xls"
                    disabled={loading || success}
                    className="hidden"
                    id="file-upload"
                  />
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <FaFileUpload className="text-4xl text-slate-400 mx-auto mb-3" />
                    <p className="text-sm font-medium text-slate-700">
                      {evidencia ? (
                        <span className="text-blue-600 flex items-center justify-center gap-2">
                          <FaCheckCircle /> {evidencia.name}
                        </span>
                      ) : (
                        'Click para seleccionar archivo'
                      )}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      PDF, DOC, DOCX, ZIP, PNG, JPG, JPEG, XLS, XLSX (máx. 10MB)
                    </p>
                  </label>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                  className="flex-1 bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold px-6 py-3 rounded-xl transition-colors disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading || success}
                  className="flex-1 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-semibold px-6 py-3 rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <FaSpinner className="animate-spin" />
                      Enviando...
                    </>
                  ) : success ? (
                    <>
                      <FaCheckCircle />
                      Enviado
                    </>
                  ) : (
                    <>
                      <FaPaperPlane />
                      Enviar Tarea
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {activeTab === 'plazo' && (
            <form onSubmit={handleSolicitarPlazo} className="space-y-6">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="text-sm text-amber-800 flex items-start gap-2">
                  <FaInfoCircle className="mt-0.5 flex-shrink-0" />
                  <span>
                    Solicita una nueva fecha límite y explica el motivo. Esto no genera multas ni bonos.
                  </span>
                </p>
              </div>

              {String(task.estado_plazo || '').toUpperCase() === 'PENDIENTE' && (
                <div className="bg-amber-100 border border-amber-300 rounded-xl p-4 flex items-start gap-2 text-amber-900">
                  <FaExclamationCircle className="text-xl mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold">Solicitud de plazo pendiente</p>
                    <p className="text-xs">Ya enviaste una solicitud y está a la espera de respuesta del jefe{plazoSolicitadoHace ? ` · hace ${plazoSolicitadoHace}` : ''}.</p>
                  </div>
                </div>
              )}

              {success && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-2 text-emerald-800">
                  <FaCheckCircle className="text-xl" />
                  <span className="font-medium">Solicitud enviada exitosamente</span>
                </div>
              )}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-2 text-red-800">
                  <FaExclamationCircle className="text-xl mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Nueva fecha límite <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={nuevaFecha}
                    onChange={(e) => setNuevaFecha(e.target.value)}
                    required
                    disabled={loading || success || isPlazoPending}
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent disabled:bg-slate-50 disabled:text-slate-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Fecha actual
                  </label>
                  <p className="px-4 py-3 border border-slate-200 rounded-xl bg-slate-50 text-slate-700">
                    {task.fecha_termino 
                      ? formatDate(task.fecha_termino)
                      : 'Sin fecha límite'}
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Motivo de la solicitud <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  placeholder="Explica brevemente la razón del cambio de fecha"
                  rows={5}
                  required
                  disabled={loading || success || isPlazoPending}
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none disabled:bg-slate-50 disabled:text-slate-500"
                />
                <p className="text-xs text-slate-500 mt-1">Mínimo 10 caracteres</p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                  className="flex-1 bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold px-6 py-3 rounded-xl transition-colors disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading || success || isPlazoPending}
                  className="flex-1 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold px-6 py-3 rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <FaSpinner className="animate-spin" />
                      Enviando...
                    </>
                  ) : success ? (
                    <>
                      <FaCheckCircle />
                      Enviado
                    </>
                  ) : (
                    <>
                      <FaClock />
                      Solicitar Plazo
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          
        </div>
      </div>
    </div>
  );
}
  const formatDate = (s) => {
    const a = String(s || '')
    const part = a.split('T')[0].split(' ')[0]
    if (!part) return '—'
    const [y, m, d] = part.split('-')
    return `${d}/${m}/${y}`
  }
